import { t } from 'i18next';
import { ReactNode } from 'react';
import { useTheme } from 'styled-components';

import { AppStatus, AppStatusIcon } from '#/service/constants';
import { ErrorCode } from '#/service/error/errors';
import { PartialDictionary } from '#/service/utils';
import { useTranslations } from '#/store/translation/translation.store';

import {
  BackContainer,
  BackImage,
  DialogButton,
  DialogDiv,
  OverlayDiv,
  StatusError,
  StatusIcon,
  StatusTitle,
} from './styled';

export interface StatusProps {
  /**
   * @optional Background image URL.
   * If not provided, the default background color based on `displayAs` will be used
   * */
  backgroundImageUrl?: string;

  /**
   * @default true Applies a statically defined linear gradient on the background image
   * Disable this if you want to use background image with a gradient already applied
   * It does make sense to use it only with `backgroundImageUrl` provided
   * */
  applyDefaultGradient?: boolean;

  /** @default false */
  fatalError?: boolean;

  /** @default AppStatus.Active Application status */
  status?: AppStatus;

  /** Status title text. If left empty a translation dependent on the chosen `status` will be used */
  statusTitle?: string;

  /** Status text message. If left empty a translation dependent on the chosen `status` will be used */
  statusMessage?: string;

  /** Text displayed on the dismiss button */
  dismissMessage?: string;

  /** Error code */
  errorCode?: ErrorCode;

  /** Callback function for dismissing the component */
  onDismiss?: () => void;

  /** Determines how your Status message will be show.
   * `page` makes it in an opaque full-page container.
   * `dialog` makes it in a slightly transparent container.
   * This is to align with the Colors-keys guide in a non-breaking way
   * @default 'page'
   **/
  displayAs?: 'page' | 'dialog';

  /**
   * Custom contents of the Status component. It will be displayed below the Status title.
   * If you use children prop, then `statusMessage` and dismiss props will be ignored.
   */
  children?: ReactNode;
}

// default translations
const translationMapping: PartialDictionary<AppStatus, string> = {
  [AppStatus.GeoBlocked]: 'app.geoRestriction',
  [AppStatus.Maintenance]: 'app.maintenance',
  [AppStatus.AccessDenied]: 'error.authenticationRequired',
};

/** Dialog to display information about the application status. */
export function Status({
  fatalError = false,
  status = AppStatus.Active,
  statusTitle = '',
  statusMessage = '',
  dismissMessage = '',
  errorCode,
  onDismiss,
  backgroundImageUrl,
  applyDefaultGradient = true,
  displayAs = 'page',
  children,
}: Readonly<StatusProps>) {
  const theme = useTheme();

  // just to make sure we re-render when translations change.
  // ex: we render app-wide geo-blocked status before the translations were initialised.
  useTranslations();

  const {
    imageOverlayBackground = '',
    pageBackground,
    dialogHeadingForeground = '#FFF',
    dialogParagraphForeground = '#FFF',
    pageHeadingForeground,
    pageParagraphForeground,
  } = theme.color;

  // use page or dialog colors depending on `displayAs`
  const [backgroundColor, headingColor, paragraphColor] =
    displayAs === 'page'
      ? [pageBackground, pageHeadingForeground, pageParagraphForeground]
      : [
          imageOverlayBackground,
          dialogHeadingForeground,
          dialogParagraphForeground,
        ];

  if (status === AppStatus.Active && !fatalError) {
    return null;
  }

  const appStatus = status === AppStatus.Active ? AppStatus.Error : status;
  const translationKey = translationMapping[appStatus] || 'error.generic';

  const info = {
    title: statusTitle || t(`${translationKey}.title`),
    message: statusMessage || t(`${translationKey}.message`),
    dismiss: dismissMessage || t('button.dismiss'),
  };

  const statusIcon = AppStatusIcon[appStatus];

  return (
    <OverlayDiv {...{ backgroundColor, color: paragraphColor }} role="alert">
      {backgroundImageUrl && (
        <BackContainer>
          <BackImage
            data-apply-gradient={applyDefaultGradient}
            src={backgroundImageUrl}
          />
        </BackContainer>
      )}
      <DialogDiv data-testid="status">
        {statusIcon && <StatusIcon color={headingColor} name={statusIcon} />}
        <StatusTitle color={headingColor}>{info.title}</StatusTitle>
        {children ?? (
          <>
            <p>{info.message}</p>
            {errorCode && <StatusError>{errorCode}</StatusError>}
            {onDismiss && (
              <DialogButton onClick={onDismiss}>{info.dismiss}</DialogButton>
            )}
          </>
        )}
      </DialogDiv>
    </OverlayDiv>
  );
}
